/* eslint-disable no-param-reassign */
import React, {useEffect, useState, useCallback, useContext} from 'react';
import _ from 'lodash';
import appConfig from 'app-config';
import {ViewModelForm, useDataRefresh, ViewModelServiceContext} from '@xengage/gw-portals-viewmodel-react';
import {useDependencies} from '@xengage/gw-portals-dependency-react';
import {useAuthentication} from '@xengage/gw-digital-auth-react';
import {useValidation} from '@xengage/gw-portals-validation-react';
import {WizardPage, wizardProps} from '@xengage/gw-portals-wizard-react';
import {FormattedMessage, useTranslator} from '@jutro/locale';
import {MLLocalDateUtil, MLProductCodeUtil} from 'ml-portals-util-js';
import {MLModifierComponent} from 'ml-components-common-react';
import {MLNotificationBlockUtil, MLWizardNavUtil} from 'ml-portals-util-react';
import {MLModalService} from 'ml-portals-services-react';
import {platformMessages} from 'ml-components-platform-react';
import {MLMultiLobsDataHolderService} from 'ml-services-common-react';
import {UserService, MLProducerEligibilityService} from 'gw-capability-gateway';
import {AccountService} from 'gw-capability-gateway-policy';
import HOTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/HOTrackEvent';
import {ScrollToError} from '@jutro/legacy/wizard-next';
import {PriorInsuranceComponent} from "ml-capability-quoteandbind-common-react";
import {MLUWIssuesPanelComponent} from "gw-capability-gateway-quoteandbind-common-react";
import { withRouter } from 'react-router-dom';
import metadata from './GatewayCompRaterHOValidateInformationPage.metadata.json5';
import messages from './GatewayCompRaterHOValidateInformationPage.messages';
import styles from './GatewayCompRaterHOValidateInformationPage.module.scss';
import { CheckboxGroupFieldInternal } from '@jutro/legacy/components/CheckboxGroupField/CheckboxGroupFieldInternal';

import { MLHVQDiscrepanciesUtil } from 'ml-portals-util-js';
import { MLHVQDiscrepancyPropertyColomnComponent } from 'gw-capability-quoteandbind-pa-react';
const occupancyQuestionCode = 'HOPPreQualOccupant';
const occTypes = {
    myself: 'HOPPreQualOccupantMyself',
    tenant: 'HOPPreQualOccupantTenant'
};

const questionsToShow = ['HOPRentHome_ML', 'HOPBusinessHome_ML', 'HOPHasTrampoline_ML', 'HOPBusinessPursuitsCoverage_ML',
    'HOPOwnPropertyDiffLocation_ML', 'HOPRentMoreThan30DaysPerYear_ML', 'HOPSubscriptionPayments_ML', 'HOPDwellingHomeSharingAirbnb_Ext'];
const questionsForAdditional = ['HOPEducationLevel_ML'];
const hopYearUpdatedModifiers = ['HOPElectricalYearUpdated_ML', 'HOPPlumbingYearUpdated_ML', 'HOPHeatingYearUpdated_ML'];

const qsForDwellingConstruction = [ "HOP_PlumbingType_Ext", "HOPWiringType_Ext"];

const filterQuestionSet = (qs) => {
    qs.answers = Object.keys(qs.answers)
        .filter((key) => questionsToShow.includes(key))
        .reduce((obj, key) => {
            obj[key] = qs.answers[key];

            return obj;
        }, {});
};

const filterAdditionalQuestionSet = (qs) => {
    qs.answers = Object.keys(qs.answers)
        .filter((key) => questionsForAdditional.includes(key))
        .reduce((obj, key) => {
            obj[key] = qs.answers[key];

            return obj;
        }, {});
};

const filterDwellingConstructionQuestionSet = (qs) => {
    qs.answers = Object.keys(qs.answers)
        .filter((key) => qsForDwellingConstruction.includes(key))
        .reduce((obj, key) => {
            obj[key] = qs.answers[key];

            return obj;
        }, {});
};

function GatewayCompRaterHOValidateInformationPage(props) {
    const {
        wizardData: submissionVM,
        updateWizardData,
        goNext,
        currentStep,
        isSkipping,
        steps,
        changeNextSteps,
        history
    } = props;

    const {authHeader} = useAuthentication();
    const {refreshData} = useDataRefresh();
    const translator = useTranslator();
    const {LoadSaveService} = useDependencies('LoadSaveService');

    const {onValidate, isComponentValid} = useValidation('GatewayCompRaterHOValidateInformationPage');

    const quoteID = submissionVM.quoteID.value;
    const isGpa = appConfig.portalCode === 'GPA';
    const productCode = submissionVM.baseData.productCode.value;
    const isHomeowners = MLProductCodeUtil.isHomeOwners(submissionVM.baseData.productCode.value);
    const deedInfo_Ext = submissionVM?.baseData?.deedInfo_Ext?.value;
    const coverageCode = submissionVM.lobData?.homeowners?.coverables?.coveragePartType_ML?.value?.code;
    const isHomeOwnersPartialTypeHome = MLProductCodeUtil.isHomeOwnersPartialTypeHome(coverageCode);
    const isHomeOwnersPartialTypeRental = MLProductCodeUtil.isHomeOwnersPartialTypeRental(coverageCode);
    const isHomeOwnersPartialTypeCondo = MLProductCodeUtil.isHomeOwnersPartialTypeCondo(coverageCode);
    const isHomeOwnersPartialTypeLandlordHome = MLProductCodeUtil.isHomeOwnersPartialTypeLandlordHome(coverageCode);
    const isHomeOwnersPartialTypeLandlordCondo = MLProductCodeUtil.isHomeOwnersPartialTypeLandlordCondo(coverageCode);
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const isModernHomeOrCondo = isModernProduct && (isHomeOwnersPartialTypeHome || isHomeOwnersPartialTypeCondo);
    const isModernHomeOrCondoOrRenters = isModernProduct && (isHomeOwnersPartialTypeHome || isHomeOwnersPartialTypeCondo || isHomeOwnersPartialTypeRental);
    const isModernLLCOrLLH = isModernProduct && (isHomeOwnersPartialTypeLandlordHome || isHomeOwnersPartialTypeLandlordCondo);
    const questionSetCode = isHomeowners ? 'HOPPreQual' : 'PUPPreQual_PUE';
    const preQualQuestionSetPath = `lobData.homeowners.preQualQuestionSets.value[0]`;
    const [inSync, setInSync] = useState(false);
    const [dateModifiersInvalid, updateDateModifiersInvalid] = useState([]);
    const modifiers = _.get(submissionVM, 'lobData.homeowners.modifiers_ML');
    const isRentalPolicy = _.get(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value.code') === 'hoprental';
    const homeownerCoverablesVM = _.get(submissionVM, 'lobData.homeowners.coverables');
    const constructionDetailsVM = _.get(homeownerCoverablesVM, 'constructionDetails_ML');
    const coveragePartType = _.get(homeownerCoverablesVM, 'coveragePartType_ML.value');
    const viewModelService = useContext(ViewModelServiceContext);
    const [showErrors, setShowErrors, updateShowErrors] = useState(false);
    const [showElectronicToPaperWarning, setShowElectronicToPaperWarning] = useState(false);
    const [priorInsuranceErrors_Ext, setPriorInsuranceErrors_Ext] = useState([]);
    const [showPriorInsuranceErrors_Ext, setShowPriorInsuranceErrors_Ext] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const {nextStepVisited} = MLWizardNavUtil(props);
    const multipolicyLobCodes = ['HOPMultiPolicyNumber_ML', 'HOPPrimaryResidenceWithMetLifePolicyNumber_ML', 'HOPHomeOrLandlordHomePolicyNumber_ML', 'HOPLandlordHomePolicyNumber_ML']
    const [displayEmailWarning, setDisplayEmailWarning] = useState(false);
    const [showInvalidEmailError, setShowInvalidEmailError] = useState(false);
    const [isUWIssuesPanelVisible, setIsUWIssuesPanelVisible] = useState(false);
    const [blockingUwIssues, setBlockingUwIssues] = useState([]);
    const [disableNextButton, setDisableNextButton] = useState(false);
    const [discrepancyVMs] = useState([]);
    const {
        showProgressDialog,
        hideProgressDialog,
        showErrorModal
    } = MLModalService();

    const {
        clearBlockAlerts,
        clearErrorAlerts,
        addErrorAlert,
        addWarningAlert,
        getNumberOfAlerts
    } = MLNotificationBlockUtil();

    // Home discrepancy
    const [discrepancyCategories, setDiscrepancyCategories] = useState([]);
    
    const [discrepancyUpdated, setDiscrepancyUpdated] = useState(false);
    const discrepanciesVM = _.get(discrepancyCategories, 'dwellings', []);
    const {
    getLOBPendingDiscrepancyItemVMs,
    getLOBDiscrepancyItemVMs,
    markAllDiscrepancyItemsCompleted,
    getPropertyVMsFromVMItemWithDefaultSelection,
    isDwellingItemVM
    } = MLHVQDiscrepanciesUtil();

    const isHOPDwellingModernProduct = isModernProduct && _.get(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value.code') === 'hopdwelling';
    const isLandlordHome = MLProductCodeUtil.isHomeOwnersPartialTypeLandlordHome(submissionVM.baseData.productCode.value);
    const isLandlordCondo = MLProductCodeUtil.isHomeOwnersPartialTypeLandlordCondo(submissionVM.baseData.productCode.value);

    const [tenantScreeningValues, updateTenantScreeningValues] = useState([]);
    const [inValidOptionSelected, updateInValidOptionSelected] = useState(undefined);
    const [occupationtypelist, updateOccupationtypelist] = useState();

    const occupancyType = _.get(submissionVM, 'lobData.homeowners.preQualQuestionSets.value[0].answers.HOPPreQualLandlordDwellingOccupancy_Ext' , undefined);
    
    const isOccupancyVacant = occupancyType === 'HOPPreQualLandlordOccupancyVacant';

    const HOP_POLICY_NUMBER_MODIFIERS = [
        'HOPMultiPolicyNumberAuto_Ext',
        'HOPAnotherPrimSeasSecPropertyPolicyNumber_Ext',
        'HOPPrimSeasSecPropertyPolicyNumber_Ext',
        'HOPMultiPolicyNumberLandlord_Ext',
        'HOPMultiPolicyNumberUmbrella_Ext',
        'HOPMultiPolicyNumberBoat_Ext',
        'HOPMultiPolicyNumberMotorcycle_Ext',
        'HOPMultiPolicyNumberRecreationalVehicle_Ext',
        'HOPMultiPolicyNumberOffRoadVehicle_Ext'
    ];


    if (modifiers && modifiers.lineModifiers_ML.value) {
        modifiers.lineModifiers_ML.value.forEach(
            (mod) => {
                if (multipolicyLobCodes.includes(mod?.codeIdentifier_ML) || HOP_POLICY_NUMBER_MODIFIERS.includes(mod?.codeIdentifier_ML)) {
                    mod.isVisible_ML = false
                    mod.isInputValueRequired_ML = false
                    mod.editMode_ML = false
                    mod.savedFromPortal = false
                } else {
                    mod.isVisible_ML = true
                }
            }
        );
    }

    useEffect(() => {
        const sniPriorMailingAddressValue = _.get(submissionVM, 'baseData.sniPriorAddressQuestion_Ext.value', undefined)
        if (_.isUndefined(sniPriorMailingAddressValue)) {
            _.set(submissionVM, 'baseData.sniPriorAddressQuestion_Ext.value', false);
        }
        refreshData();
    }, []);

    // DE333477 - Map SolarPanelsExists from NearMap solarPanelsExistsInd and default to false when unavailable.
    useEffect(() => {
        const existingSolarPanelsExists = _.get(constructionDetailsVM, 'solarPanelsExists.value');
        if (!_.isNil(existingSolarPanelsExists)) {
            return;
        }
        const solarPanelsExistsInd = _.get(constructionDetailsVM, 'solarPanelsExistsInd.value');
        _.set(
            constructionDetailsVM,
            'solarPanelsExists.value',
            _.isNil(solarPanelsExistsInd) ? false : solarPanelsExistsInd
        );
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const initOccupationTypeList = () => {
            let categories;
            if (!isModernProduct) return;
            categories = submissionVM.baseData.primaryNamedInsured_ML.occupationType_Ext.aspects.availableValues.filter((value) => {
                return value.code.endsWith('_np') || value.code === 'military' || value.code === 'other';
            });

            const categoryCodes = categories.map((category) => ({
                code: category.code,
                name: translator({
                    id: category.name,
                    defaultMessage: category.name
                })
            }));

            updateOccupationtypelist(categoryCodes);
        }

        initOccupationTypeList();

    }, [isModernProduct]);

    const hydrant1000FtValue = _.get(constructionDetailsVM, 'hydrantWithin1000Feet.value');
        const fireDistrictInitialValues = _.get(constructionDetailsVM, 'fireDistrictNames.value', []);
        const fireDistrictName = _.get(constructionDetailsVM, 'fireDistrictName.value', '');
        const addressMatch = _.get(constructionDetailsVM, 'matchUsing.value.code', undefined);
        const hasWaterSupply = _.get(constructionDetailsVM, 'waterSupply.value', undefined);
        const hasSplitPPC = _.get(constructionDetailsVM, 'splitPPC.value', undefined);
        const hasFireDistrictName = _.get(constructionDetailsVM, 'fireDistrictName.value', undefined);
        const[fireDistrictValues, setFireDistrictValues]  = useState(fireDistrictInitialValues);
    
        const fireDistrictDropdownValues = fireDistrictValues.length >= 1
            ? fireDistrictValues
            : fireDistrictName
                ? [fireDistrictName]
                : [];
    
        const isRefreshBtnVisible = () => {
            return addressMatch?.toLowerCase() === 'z' && fireDistrictName !== '';
        };
    
        const isHydrantValueDisabled = () => {
            const val1 = addressMatch?.toLowerCase() === 'a' && !_.isUndefined(hasSplitPPC) && !_.isUndefined(hasWaterSupply) && hasWaterSupply == '-';
            const val2 = addressMatch?.toLowerCase() === 'z' && !_.isUndefined(hasSplitPPC) && fireDistrictName !== '';
    
            return !(val1 || val2);
        };
    
        const [selectedHydrantValue, setSelectedHydrantValue] = useState(hydrant1000FtValue);
        const [isFireDistrictEditable, setIsFireDistrictEditable] = useState((!fireDistrictName || fireDistrictName == '') && fireDistrictValues.length >= 1);
        const [isHydrantEditable, setIsHydrantEditable] = useState(!isHydrantValueDisabled());
        const [isRefreshButtonVisible, setIsRefreshButtonVisible] = useState(isRefreshBtnVisible());
        
        const [selectedFireDistrictValue, setSelectedFireDistrictValue] = useState(fireDistrictDropdownValues.find(item =>
            item.split(';')[0].trim() === fireDistrictName
        ) || (fireDistrictName ?? ''));


    const getEarthquakeCoverage = () =>
        submissionVM?.value?.lobData?.homeowners?.offerings[0]?.coverages?.optionalCoverages_ML?.find(cov => cov.code_ML === "HOPEarthquakeCov_Ext");

    const isEarthquakeSelected = () =>
        getEarthquakeCoverage()?.selected === true;


    const getEarthquakeCoverageML = () => {
        const offerings =
            submissionVM?.value?.lobData?.homeowners?.offerings || [];

        for (const offering of offerings) {
            const earthquakeCoverage =
                offering?.coverages?.optionalCoverages_ML?.find(
                    coverage => coverage.code_ML === 'HOPEarthquakeCov_Ext'
                );

            if (earthquakeCoverage) {
                return {
                    offering,
                    coverage: earthquakeCoverage
                };
            }
        }

        return null;
    };

    const getEarthquakeTerm = codeIdentifier =>
        getEarthquakeCoverage()?.terms?.find(
            term => term.codeIdentifier_ML === codeIdentifier
        );

    const getTermAvailableValues = codeIdentifier =>
        getEarthquakeTerm(codeIdentifier)?.options || [];

    const getEarthquakeOffering = () => {
        const offerings = _.get(
            submissionVM,
            'value.lobData.homeowners.offerings',
            []
        );

        return offerings.find(offering =>
            offering?.coverages?.optionalCoverages_ML?.some(
                cov => cov.code_ML === 'HOPEarthquakeCov_Ext'
            )
        );
    };

    const getSelectedEarthquakeOffering = () => {
        const offerings = _.get(
            submissionVM,
            'value.lobData.homeowners.offerings',
            []
        );

        return offerings.find(offering =>
            offering?.coverages?.optionalCoverages_ML?.some(
                coverage => coverage.code_ML === 'HOPEarthquakeCov_Ext'
            )
        );
    };

    const markAllEarthquakeTermsUpdated = () => {
        const coverage = getEarthquakeCoverage();

        if (coverage?.selected) {
            _.set(coverage, 'updated', true);

            coverage.terms?.forEach(term => {
                _.set(term, 'updated', true);
            });
        }
    };

    const saveEarthquakeCoverage = async () => {
        if (!isModernProduct) return
        const offering = getSelectedEarthquakeOffering();

        markAllEarthquakeTermsUpdated()

        const earthquakeCoverage = getEarthquakeCoverage();
        if (!offering?.coverages || !earthquakeCoverage.selected) {
            return;
        }
        const earthquakeOnlyCoverages = {
            homeowners: {
                optionalCoverages_ML: [earthquakeCoverage]
            }
        };

        const updatedCoverages = await LoadSaveService.updateCoverages(
            submissionVM.value.quoteID,
            submissionVM.value.sessionUUID,
            earthquakeOnlyCoverages,
            authHeader
        );

        const returnedEarthquakeCoverage =
            updatedCoverages?.optionalCoverages_ML?.find(
                cov => cov.code_ML === 'HOPEarthquakeCov_Ext'
            );

        const earthquakeIndex =
            offering.coverages.optionalCoverages_ML?.findIndex(
                cov => cov.code_ML === 'HOPEarthquakeCov_Ext'
            );

        if (returnedEarthquakeCoverage && earthquakeIndex > -1) {
            _.set(
                offering,
                `coverages.optionalCoverages_ML[${earthquakeIndex}]`,
                returnedEarthquakeCoverage
            );
        }

        updateWizardData(submissionVM);

        return updatedCoverages;
    };

    const updateEarthquakeTerm = (codeIdentifier, value) => {
        const term = getEarthquakeTerm(codeIdentifier);
        const selectedOption = term?.options?.find(
            option => String(option.code) === String(value)
        );

        if (!term) {
            return;
        }
        if(codeIdentifier === 'HOPEarthquakeYearBuilt_Ext') {
            _.set(term, 'directValue', value);
        }
        _.set(term, 'chosenTerm', value);
        _.set(term, 'chosenTermValue', selectedOption?.name || value);
        _.set(term, 'updated', true);

        if (term.valueType === 'Other' || term.genericValue_ML !== undefined) {
            _.set(term, 'genericValue_ML', selectedOption?.name || value);
        }

        updateWizardData(submissionVM);
    };

    // Homeowners specific variables
    const [occupancyAnswer, setOccupancyAnswer] = useState(_.get(submissionVM, `lobData.homeowners.preQualQuestionSets.value[0].answers[${occupancyQuestionCode}]`));
    const [polType, setPolType] = useState(_.get(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value'));

    const getRoofInstalledYear = useCallback(() => {
        const minYear = _.get(constructionDetailsVM, 'yearBuilt.value')
        const maxYear = new Date().getFullYear();
        // _.range is not inclusive. Need to add -1 to get correct years
        const yearRange = _.range(maxYear, minYear - 1);
        const years = yearRange.map(_.toString).map((year) => ({
            code: year,
            name: year
        }));

        return years;
    }, [constructionDetailsVM]);

    const fieldValuesAreNotAvailable = useCallback(() => {
        if (isModernLLCOrLLH) {
            const tenantValue = submissionVM.lobData.homeowners.coverables.constructionDetails_ML?.tenantScreening
            const numberOfVacantRent = submissionVM.lobData.homeowners.coverables.constructionDetails_ML?.numberOfVacantRent

            if (isOccupancyVacant && tenantValue?.length > 0) {
                updateTenantScreeningValues([]);
                _.set(
                    submissionVM,
                    'lobData.homeowners.coverables.constructionDetails_ML.tenantScreening.value',
                    []
                );
            }

            if (tenantValue && tenantValue.length == 0 && !isOccupancyVacant) {
                addErrorAlert(translator(messages.missingTenant), 'missingField');
                return true
            }
            if(_.isUndefined(numberOfVacantRent.value) || (numberOfVacantRent.value < 1 || numberOfVacantRent.value > 99) || numberOfVacantRent.value === ''){
                return true
            }
            return false

        }
        return false
    }, [submissionVM.lobData.homeowners.coverables.constructionDetails_ML]);

    const inValidOptionSelectedNP = useCallback(() => {
        updateInValidOptionSelected(undefined)

        if (isModernLLCOrLLH && !isOccupancyVacant) {

            const tenantValueRaw =
                submissionVM.lobData.homeowners.coverables.constructionDetails_ML?.tenantScreening || []

            const tenantValue = Array.isArray(tenantValueRaw)
                ? tenantValueRaw
                : tenantValueRaw?.value || [];

            if (tenantValue.length === 0) {
                updateInValidOptionSelected(messages.requiredDetailsMissingError)
                return true
            }

            const hasNoneApply = tenantValue.includes('none_apply')

            // none_apply + other options selected
            if (hasNoneApply && tenantValue.length > 1) {
                updateInValidOptionSelected(messages.inValidOptionselected);
                return true
            }

            // only none_apply selected
            if (hasNoneApply && tenantValue.length === 1) {
                updateInValidOptionSelected(undefined);
                return false
            }
        }

        return false

    }, [submissionVM.lobData.homeowners.coverables.constructionDetails_ML])

    const checkForValidateInfoUwIssues = useCallback(() => {
        const uwIssues = submissionVM.errorsAndWarnings?.underwritingIssues?.value;
        if (uwIssues?.length > 0) {
            const preQuoteCheckpointIssues = uwIssues.filter((issue) => {
                return (issue.checkingSet_Ext === 'dwelling_construction_ml' || issue.checkingSet_Ext === 'Question' || issue.checkingSet_Ext === 'PreQuote') && issue.currentBlockingPoint === 'BlocksQuote';
            });
            if (preQuoteCheckpointIssues?.length > 0) {
                setIsUWIssuesPanelVisible(true);
                setBlockingUwIssues(preQuoteCheckpointIssues);
                setDisableNextButton(true);
                return true;
            }
            setDisableNextButton(false);
            return false;
        }
        return false;
    }, [submissionVM]);

    const checkForDisableNextButton = useCallback((issues) => {
        if (issues?.length > 0) {
            const prequoteCheckpointIssues = issues.filter((issue) => {
                return (issue.checkingSet_Ext === 'dwelling_construction_ml' || issue.checkingSet_Ext === 'Question' || issue.checkingSet_Ext === 'PreQuote') && issue.currentBlockingPoint === 'BlocksQuote';
            });
            setDisableNextButton(prequoteCheckpointIssues?.length > 0);
        }
    }, []);

    const writeValue = useCallback((value, path) => {
        const newSubmissionVM = viewModelService.clone(submissionVM);

        _.set(newSubmissionVM, path, value);
        updateWizardData(newSubmissionVM);
        setDisableNextButton(false);
        
    }, [submissionVM, updateWizardData, viewModelService]);

    const writeValueNP = async (value, changedPath) => {
        const newSubmissionVM = viewModelService.clone(submissionVM);
        _.set(newSubmissionVM, changedPath, value);
        updateWizardData(newSubmissionVM);
        const updateValue = await LoadSaveService.updateDwellingField(submissionVM.quoteID.value, value, authHeader);
        _.set(submissionVM, 'lobData.homeowners.coverables.constructionDetails_ML.underconstructionDwelling.value', value);
        refreshData();
        syncDwellingInfoQuestionSets('Boolean');
    };

    const updatePreQualQuestionSets = useCallback((update) => {
        const answers = _.find(update, (qs) => qs.code === questionSetCode);

        _.set(submissionVM, preQualQuestionSetPath, answers);
        updateWizardData(submissionVM);
    }, [submissionVM, updateWizardData, questionSetCode, preQualQuestionSetPath]);

    const updateSubmission = useCallback(() => {
        const updatePromise = LoadSaveService.updateDraftSubmission(submissionVM.value, authHeader);

        showProgressDialog(platformMessages.updatingSubmission, updatePromise, 0);
        updatePromise.then((sub) => {
            _.set(submissionVM, 'value', sub);
            updateWizardData(submissionVM);
            setDisableNextButton(false);
        }, () => {
            const title = translator(messages.saveSubmissionErrorTitle);
            const message = translator(messages.saveSubmissionErrorMessage);

            showErrorModal(title, message);
        });
    }, [LoadSaveService, authHeader, showErrorModal, showProgressDialog, submissionVM, translator, updateWizardData]);

    const showPolicyTypeChangedAlert = useCallback(() => {
        clearBlockAlerts('policyTypeHasChanged');

        const msg = (
            <FormattedMessage
                {...messages.policyTypeChangedWarning}
                values={{
                    firstLine: (
                        <strong>{translator(messages.policyTypeChangedWarningFirstLine)}</strong>
                    ),
                    lineBreak: <br/>,
                    secondLine: (
                        translator(messages.policyTypeChangedWarningSecondLine)
                    )
                }}
            />
        );

        addWarningAlert(msg, 'policyTypeHasChanged');
    }, [clearBlockAlerts, translator, addWarningAlert]);

    const updateJobIfPolicyTypeChanged = useCallback(() => {
        const postalCode = _.get(submissionVM, 'baseData.primaryNamedInsured_ML.primaryAddress.postalCode.value');
        const productCodeChg = _.get(submissionVM, 'baseData.productCode.value');

        MLMultiLobsDataHolderService.updateJob(quoteID, productCodeChg, postalCode, polType.code, isGpa && authHeader);
    }, [authHeader, isGpa, polType, quoteID, submissionVM]);

    const checkPolicyType = useCallback((occupancyType) => {
        let showMessage = false;
        let newPolicyType;

        switch (polType.code) {
            case MLProductCodeUtil.getDwellingCode(): {
                showMessage = occupancyType === occTypes.tenant;
                newPolicyType = {code: MLProductCodeUtil.getLandlordHome()};
                break;
            }
            case MLProductCodeUtil.getLandlordHome(): {
                showMessage = occupancyType === occTypes.myself;
                newPolicyType = {code: MLProductCodeUtil.getDwellingCode()};
                break;
            }
            case MLProductCodeUtil.getCondoCode(): {
                showMessage = occupancyType === occTypes.tenant;
                newPolicyType = {code: MLProductCodeUtil.getLandlordCondo()};
                break;
            }
            case MLProductCodeUtil.getLandlordCondo(): {
                showMessage = occupancyType === occTypes.myself;
                newPolicyType = {code: MLProductCodeUtil.getCondoCode()};
                break;
            }
            default:
                break;
        }


        if (showMessage) {
            setPolType(newPolicyType);
            _.set(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value', newPolicyType);
            showPolicyTypeChangedAlert();
            updateJobIfPolicyTypeChanged();
        }
        const dwellingsModifiers = modifiers.dwellingsModifiers_ML.value
            dwellingsModifiers.forEach((mod) => {
            	if ((mod.chosenTermValue === null || mod.chosenTermValue === undefined) && mod?.codeIdentifier_ML === 'HOP_PlumbingType_ML' )
                  mod.chosenTermValue = false;
        });
        updateSubmission();
    }, [polType, showPolicyTypeChangedAlert, submissionVM, updateJobIfPolicyTypeChanged, updateSubmission]);

    useEffect(() => {
        setIsRefreshButtonVisible(isRefreshBtnVisible());
        setIsHydrantEditable(!isHydrantValueDisabled())
    }, [constructionDetailsVM, fireDistrictName, isRefreshButtonVisible, selectedHydrantValue]);

    useEffect(() => {
        if (!nextStepVisited()) {
            const clonedSubmissionVM = _.clone(submissionVM);

            _.set(clonedSubmissionVM, 'baseData.bypassCreditOrdering_Ext.value', false);
            updateWizardData(clonedSubmissionVM)
        }

        if (_.get(submissionVM, 'baseData.policyBillingDocCommPref_ML.value.code') === 'electronic' &&
            (!_.get(submissionVM, 'baseData.primaryNamedInsured_ML.emailAddress1.value'))) {
            addWarningAlert(translator(messages.electronicToPaperWarning), 'electronicToPaper');
            setShowElectronicToPaperWarning(true);
            window.scrollTo(0, 0);
        }

        if (isGpa) {
            UserService.getCurrentUser(authHeader).then((user) => {
                HOTrackEvent('Quote Step Reached', isSkipping ? skipOnResumeIf() || nextStepVisited() : false, submissionVM, user, {step: 'Gateway CompRater HO Validation Page'});
            });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const dwellingInfoQuestionSetData = _.get(submissionVM.lobData.homeowners, 'dwellingAdditionalInfo.value[0]');
    const filteredDwellingInfoQuestionSetData = _.clone(dwellingInfoQuestionSetData);
    const filteredDwellingAdditionalQuestionSetData = _.clone(dwellingInfoQuestionSetData);

    if (filteredDwellingInfoQuestionSetData) {
        filterQuestionSet(filteredDwellingInfoQuestionSetData);
    }

    if (filteredDwellingAdditionalQuestionSetData) {
        filterAdditionalQuestionSet(filteredDwellingAdditionalQuestionSetData);
    }

    const dwellingConstructionInfoQuestionSetData = _.get(submissionVM.lobData.homeowners, 'dwellingConstructionAdditionalInfo.value[0]');
    const filteredDwellingConstructionInfoQuestionSetData = _.clone(dwellingConstructionInfoQuestionSetData);

    if (filteredDwellingConstructionInfoQuestionSetData) {
        filterDwellingConstructionQuestionSet(filteredDwellingConstructionInfoQuestionSetData);
    }

    const isCondoType = () => {
        return coveragePartType.code === 'hopcondo' || coveragePartType.code === 'hop_landlord_condo_ml';
    };

    const priorInsuranceQuestionSetData = _.get(submissionVM.lobData.homeowners, 'priorInsurance.value[0]');

    const syncPriorInsuranceQuestionSets = useCallback(() => {
        const syncPromise = LoadSaveService.syncQuestionSetML(quoteID, [priorInsuranceQuestionSetData], authHeader);
 
        showProgressDialog(platformMessages.syncingQuestions, syncPromise, true, 0);

        syncPromise.then((updatedQuestionSets) => {
            const priorInsuranceQs = updatedQuestionSets.find((q) => q.code === 'PriorInsuranceMultiLine_Ext');

            _.set(submissionVM, 'lobData.homeowners.priorInsurance.value[0]', priorInsuranceQs);

            refreshData();
        });
    }, [LoadSaveService, quoteID, priorInsuranceQuestionSetData, authHeader, showProgressDialog, submissionVM, refreshData]);

    const syncDwellingInfoQuestionSets = (qsQuestionType) => {
        const syncPromise = LoadSaveService.syncQuestionSetML(quoteID, [filteredDwellingInfoQuestionSetData], authHeader);

        showProgressDialog(platformMessages.syncingQuestions, syncPromise, true, 0);

        syncPromise.then((updatedQuestionSets) => {
            const dwellingQs = updatedQuestionSets.find((q) => q.code === 'HOPDwellingAddlInfo_ML');
            const prevDwellingQuestionSet = _.get(submissionVM, 'lobData.homeowners.dwellingAdditionalInfo.value[0]', {});

            _.set(submissionVM, 'lobData.homeowners.dwellingAdditionalInfo.value[0]', dwellingQs);
            // Syncing Education additional question with the previous value during other question set questions getting synced in the page
            _.set(submissionVM, 'lobData.homeowners.dwellingAdditionalInfo.value[0].answers.HOPEducationLevel_ML', prevDwellingQuestionSet.answers.HOPEducationLevel_ML);

            updateWizardData(submissionVM);
            setDisableNextButton(false);
            refreshData();
        });
    };

    const onChangeTypes = ['Boolean', 'Choice', 'Integer'];
    const onDwellingInfoQuestionValueChange = (aQuestionSetAnswersValue, pathToAnswers, qsQuestionType) => {
        if (onChangeTypes.includes(qsQuestionType)) {
            // RadioButton has no onBlur event
            syncDwellingInfoQuestionSets(qsQuestionType);
        } else {
            // use onBlur for other Input fields
            const prevDwellingQuestionSet = _.get(submissionVM, 'lobData.homeowners.dwellingAdditionalInfo.value[0]', {});

            _.set(submissionVM, 'lobData.homeowners.dwellingAdditionalInfo.value[0]', aQuestionSetAnswersValue);
            // Syncing Education additional question with the previous value during other question set questions getting synced in the page
            _.set(submissionVM, 'lobData.homeowners.dwellingAdditionalInfo.value[0].answers.HOPEducationLevel_ML', prevDwellingQuestionSet.answers.HOPEducationLevel_ML);
            refreshData();
            setDisableNextButton(false);
        }
    };

    const onBlurDwellingInfoQuestionValueChange = (evt, object, qsQuestionType) => {
        // check if progress dialog is causing the onBlur event and if so, do not trigger another sync call
        const isTargetModal = evt?.relatedTarget?.className?.indexOf('ml-modal-loading') > -1;
        if (!onChangeTypes.includes(qsQuestionType) && !isTargetModal) {
            // DropdownField component triggers onBlur event but syncQuestionSet should be performed in onValueChange
            syncDwellingInfoQuestionSets(qsQuestionType);
        }
    };

    const syncDwellingAdditionalQuestionSets = (qsQuestionType) => {
        const combinedHOPDwellingAddlInfoQuestionSets = { ...filteredDwellingAdditionalQuestionSetData, answers: {...filteredDwellingAdditionalQuestionSetData.answers, ...filteredDwellingInfoQuestionSetData.answers}};
        // removing syncQuestionSetML as on only Education will comes under DwellingAdditional info(remaining got filtered out)
        
        _.set(submissionVM, 'lobData.homeowners.dwellingAdditionalInfo.value[0]', combinedHOPDwellingAddlInfoQuestionSets);

        updateWizardData(submissionVM);

        refreshData();
    };
    const onDwellingAdditionalQuestionValueChange = (aQuestionSetAnswersValue, pathToAnswers, qsQuestionType) => {
        if (onChangeTypes.includes(qsQuestionType)) {
            // RadioButton has no onBlur event
            syncDwellingAdditionalQuestionSets(qsQuestionType);
        } else {
            // use onBlur for other Input fields
            _.set(submissionVM, 'lobData.homeowners.dwellingAdditionalInfo.value[0]', aQuestionSetAnswersValue);
            refreshData();
        }
    };

    const onBlurDwellingAdditionalQuestionValueChange = (evt, object, qsQuestionType) => {
        // check if progress dialog is causing the onBlur event and if so, do not trigger another sync call
        const isTargetModal = evt?.relatedTarget?.className?.indexOf('ml-modal-loading') > -1;
        if (!onChangeTypes.includes(qsQuestionType) && !isTargetModal) {
            // DropdownField component triggers onBlur event but syncQuestionSet should be performed in onValueChange
            syncDwellingAdditionalQuestionSets(qsQuestionType);
        }
    };

    const syncDwellingConsstructionInfoQuestionSets = (qsQuestionType) => {
        const syncPromise = LoadSaveService.syncQuestionSetML(quoteID, [filteredDwellingConstructionInfoQuestionSetData], authHeader);
        syncPromise.then((updatedQuestionSets) => {
            const dwellingQs = updatedQuestionSets.find((q) => q.code === 'HOPDwellingConstructionAddInfo_Ext');

            _.set(submissionVM, 'lobData.homeowners.dwellingConstructionAdditionalInfo.value[0]', dwellingQs);
            updateWizardData(submissionVM);
            refreshData();
        });
        
        
    };

    const onDwellingConsstructionInfoValueChange = (aQuestionSetAnswersValue, pathToAnswers, qsQuestionType) => {
        if (onChangeTypes.includes(qsQuestionType)) {
            syncDwellingConsstructionInfoQuestionSets(qsQuestionType);
        }
        else {
            _.set(submissionVM, 'lobData.homeowners.dwellingConstructionAdditionalInfo.value[0]', aQuestionSetAnswersValue);
            refreshData();
        }
    };

    const onBlurDwellingConstructionAdditionalQuestionValueChange = (evt, object, qsQuestionType) => {
        // check if progress dialog is causing the onBlur event and if so, do not trigger another sync call
        const isTargetModal = evt?.relatedTarget?.className?.indexOf('ml-modal-loading') > -1;
        if (!onChangeTypes.includes(qsQuestionType) && !isTargetModal) {
            // DropdownField component triggers onBlur event but syncQuestionSet should be performed in onValueChange
            syncDwellingConsstructionInfoQuestionSets(qsQuestionType);
        }
    };

    const syncModifiers = (submissionData, mlModifier) => {
        submissionData?.forEach((mod) => {
            const oldMod = mlModifier.value.find((value) => value.patternCode === mod.patternCode);

            if (mod?.codeIdentifier_ML === "HOPMultiPolicy_ML" && mod.chosenTermValue === "true") {
                _.set(submissionVM, 'lobData.homeowners.preQualQuestionSets.value[0].answers.HOPPreQualAutoPolicyExists_Ext', true);
            }

            if (mod?.codeIdentifier_ML === "HOPMultiPolicy_ML" && mod.chosenTermValue === "false") {
                _.set(submissionVM, 'lobData.homeowners.preQualQuestionSets.value[0].answers.HOPPreQualAutoPolicyExists_Ext', false);
            }

            if (mod?.codeIdentifier_ML !== "HOPMultiPolicy_ML") {
                _.set(mod, 'chosenTermValue', oldMod ? oldMod.chosenTermValue : null);
            }
            
            if ((!isModernProduct && !multipolicyLobCodes.includes(mod?.codeIdentifier_ML)) || (isModernProduct && !HOP_POLICY_NUMBER_MODIFIERS.includes(mod?.codeIdentifier_ML))) {
                _.set(mod, 'savedFromPortal', true);
                _.set(mod, 'editMode_ML', true);
            }
        });
        _.set(mlModifier, 'value', submissionData);
    };

    const updateAndSyncModifier = (mod, modal) => {
        _.set(mod, 'updated.value', true);

        const validLineMods = [];
        const validDwellingMods = [];
        const copiedModel = _.cloneDeep(submissionVM.value);

        for (let i = 0; i < modifiers.lineModifiers_ML.children.length; i += 1) {
            const modifier = modifiers.lineModifiers_ML.children[i];

            if (modifier.aspects.subtreeValid) {
                validLineMods.push(modifier.value);
            }
        }

        for (let i = 0; i < modifiers.dwellingsModifiers_ML.children.length; i += 1) {
            const modifier = modifiers.dwellingsModifiers_ML.children[i];

            if (modifier.aspects.subtreeValid) {
                validDwellingMods.push(modifier.value);
            }
        }

        _.set(copiedModel, 'lobData.homeowners.modifiers_ML.lineModifiers_ML', validLineMods);
        _.set(copiedModel, 'lobData.homeowners.modifiers_ML.dwellingsModifiers_ML', validDwellingMods);

        if (!inSync) {
            // "updateModifiers" was called multiple times so that it has caused CDCE error.
            // Here it controls to make sure to call one at a time
            const submissionLoadPromise = LoadSaveService.updateModifiers(copiedModel, authHeader);

            setInSync(true);

            if (modal) {
                showProgressDialog(platformMessages.syncingDiscounts, submissionLoadPromise, true, 0);
            }

            submissionLoadPromise.then((submissionData) => {
                syncModifiers(submissionData.lobData.homeowners.modifiers_ML.lineModifiers_ML, modifiers.lineModifiers_ML);
                syncModifiers(submissionData.lobData.homeowners.modifiers_ML.dwellingsModifiers_ML, modifiers.dwellingsModifiers_ML);
            }, (error) => {
                // eslint-disable-next-line no-console
                console.error(error);
            }).finally(() => {
                setInSync(false);
            });
        }

        updateWizardData(submissionVM);
        setDisableNextButton(false);
    };

    const isModifierTypeDate = (mod) => mod?.valueType?.value === 'date' && mod?.dateValue_ML;

    const isModifierTypeTypeKey = (mod) => mod?.valueType?.value === 'TypeKeyAsText';

    const isDateModifierPeriodValid = (mod) => mod.dateValue_ML.year.value >= 1900 && mod.dateValue_ML.year.value < 2100
        && mod.dateValue_ML.aspects && mod.dateValue_ML.aspects.valid;


    const addAlertIfDateModifiersArePriorToYearBuilt = (mod) => {
        const clonedDateModifiersInvalid = [...dateModifiersInvalid];
        const alertAlreadyExists = clonedDateModifiersInvalid.includes(mod.name.value);

        if (mod.dateValue_ML.value.year < submissionVM.value.lobData.homeowners.coverables.constructionVeriskDetails_ML.yearBuilt && !alertAlreadyExists) {
            clonedDateModifiersInvalid.push(mod.name.value);
        } else if (alertAlreadyExists) {
            clonedDateModifiersInvalid.splice(clonedDateModifiersInvalid.indexOf(mod.name.value), 1);
        }

        updateDateModifiersInvalid(clonedDateModifiersInvalid);
    };

    const isDwellingModYearPriorToYearBuilt = (mod) => mod?.dateValue_ML?.value?.year < submissionVM.value.lobData.homeowners.coverables.constructionVeriskDetails_ML.yearBuilt
    const isDwellingModYearAfterTodaysYear = (mod) => {
        const today = new Date();

        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();

        return mod?.dateValue_ML?.value?.year > currentYear || (mod?.dateValue_ML?.value?.year === currentYear && mod?.dateValue_ML?.value?.month > currentMonth);
    }
    
    const validatePriorDateModifiersOnContinue = (dwellingMods) => {
        const dateErrors = [];

        dwellingMods.forEach((mod) => {
            if (hopYearUpdatedModifiers.includes(mod?.value?.codeIdentifier_ML)){
                if (isDwellingModYearPriorToYearBuilt(mod)) {
                    dateErrors.push(mod.name.value);
                }
            }
        })

        return dateErrors;
    };

    const validateFutureDateModifiersOnContinue = (dwellingMods) => {
        const dateErrors = [];

        dwellingMods.forEach((mod) => {
            if (hopYearUpdatedModifiers.includes(mod?.value?.codeIdentifier_ML)){
                if (isDwellingModYearAfterTodaysYear(mod)) {
                    dateErrors.push(mod.name.value);
                }
            }
        })

        return dateErrors;
    };

    const isADeviceSelected = (value) => {
        const dwellings = modifiers.dwellingsModifiers_ML.value;

        return typeof (dwellings.find((m) => m.chosenTermValue === value)) !== 'undefined';
    };

    const areHoneywellDevicesSelected = () => (isADeviceSelected('self_mon_honeywell_lyric') || isADeviceSelected('honeywell_lyric'));

    const setRemainderWarning = () => {
        clearBlockAlerts('SmartHomeValidationEmail');

        if (areHoneywellDevicesSelected()) {
            const emailAddress = _.get(submissionVM, 'baseData.primaryNamedInsured_ML.emailAddress1.value');
            const warningMsg = (
                <FormattedMessage
                    {...messages.warningMessage}
                    values={{
                        importantReminder: <strong>{translator(messages.importantReminder)}</strong>,
                        confirmEmailAddress: translator(messages.confirmEmailAddress, {email: emailAddress})
                    }}
                />
            );

            addWarningAlert(warningMsg, 'SmartHomeValidationEmail');
        }
    };

    const updateModifier = (mod, modal = true) => {
        _.set(submissionVM, 'lobData.homeowners.coverables.constructionDetails_ML.savedFromPortal.value', false);

        if (isModifierTypeDate(mod)) {
            if (isDateModifierPeriodValid(mod)) {
                updateAndSyncModifier(mod, modal);

                if (hopYearUpdatedModifiers.includes(mod?.value?.codeIdentifier_ML) && !isDwellingModYearPriorToYearBuilt(mod)){
                    clearBlockAlerts(`${mod.name.value} - Prior`);
                }
                if (hopYearUpdatedModifiers.includes(mod?.value?.codeIdentifier_ML) && !isDwellingModYearAfterTodaysYear(mod)){
                    clearBlockAlerts(`${mod.name.value} - After`);
                }

                addAlertIfDateModifiersArePriorToYearBuilt(mod);
            }
        } else if (!isModifierTypeTypeKey(mod)) {
                updateAndSyncModifier(mod, modal);
        }

        if (isGpa) {
            setRemainderWarning();
        }
    };

    const syncPreQualQuestionSets = useCallback((questionSetAnswers) => {
        if (isHomeowners) {
            const currentOccupancyAnswer = questionSetAnswers?.answers[occupancyQuestionCode];

            if (occupancyAnswer !== currentOccupancyAnswer) {
                setOccupancyAnswer(currentOccupancyAnswer);
                checkPolicyType(currentOccupancyAnswer);

                return;
            }
        }

        const syncPromise = LoadSaveService.syncQuestionSetML(quoteID, [questionSetAnswers], authHeader);

        showProgressDialog(platformMessages.syncingQuestions, syncPromise, true, 0);
        syncPromise.then(
            (updatedQuestionSets) => {
                updatePreQualQuestionSets(updatedQuestionSets);
                updateModifier();
            }
        )
        .finally(() => {
            setDisableNextButton(false);
        });
    }, [isHomeowners, LoadSaveService, quoteID, authHeader, showProgressDialog, occupancyAnswer, checkPolicyType, updatePreQualQuestionSets, updateModifier]);

    const checkReadOnlyAnswer = (key) => {
        return false;
    };

    const disableEmailField = () => {
        return (isGpa && !_.get(submissionVM, 'canEditCommunicationsFields_Ext.value') && _.get(submissionVM, 'baseData.value.originalEmail'));
    };

    const validateEmail = async (value, changedPath) => {
        const account = _.get(submissionVM,'baseData.primaryNamedInsured_ML');
        return await validateEmailAddress(account);
    };

    const onEmailAddressBlurred = () => {
        clearBlockAlerts('electronicToPaper');
        setShowElectronicToPaperWarning(false);

        if (_.get(submissionVM, 'baseData.policyBillingDocCommPref_ML.value.code') === 'electronic' &&
            !_.get(submissionVM, 'baseData.primaryNamedInsured_ML.emailAddress1.value')) {
            addWarningAlert(translator(messages.electronicToPaperWarning), 'electronicToPaper');
            setShowElectronicToPaperWarning(true);
            window.scrollTo(0, 0);
        }

        // emailOnBlur();
    }

    const emailTrim = ( event ) => {
        let emailAddress1 =  event.target.value;
        if(emailAddress1 !== "" && emailAddress1 !== undefined){
            emailAddress1 = emailAddress1.trim();
            event.target.value = emailAddress1
        }
        let email = _.get(submissionVM,'baseData.primaryNamedInsured_ML.emailAddress1.value');
        if( email !== "" && email !== undefined){
            email = email.trim();
            _.set(submissionVM,'baseData.primaryNamedInsured_ML.emailAddress1.value', email);
        }
    }

    const validateEmailAddress = async ( accountContactDetailsVm ) => {
        const account = accountContactDetailsVm;
        let emailAddress1 = _.get(account, 'emailAddress1.value');
        if (emailAddress1) {
            emailAddress1 = emailAddress1.trim();
        }
        const isValidEmailEntered = /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/.test(emailAddress1)
    
        if (isValidEmailEntered) {
            const checkEmailVerified_Ext = AccountService.checkEmailVerified_Ext(emailAddress1, authHeader);
            try {
                showProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                const res = await checkEmailVerified_Ext;
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log("Email verification API Called");
                if (res === true) {
                    _.set(account, 'emailVerified_Ext.value', true);
                    _.set(account, 'emailAddress1.value', emailAddress1);
                    return true;
                } else {
                    _.set(account, 'emailVerified_Ext.value', false);
                    _.set(account, 'emailAddress1.value', emailAddress1 + ' ');
                    return true;
                }
            } catch (e) {
                clearBlockAlerts('invalidEmail');
                addWarningAlert(translator(platformMessages.invalidEmail), 'invalidEmail');
                _.set(account, 'emailVerified_Ext.value', false);
                _.set(account, 'emailAddress1.value', emailAddress1);
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log(e);
                window.scrollTo(0, 0);
                if(!displayEmailWarning){
                    setDisplayEmailWarning(true);
                    return false;
                }else
                    return true;
            }
        } else {
            if (emailAddress1 !== undefined && emailAddress1 !== "") {
                setShowInvalidEmailError(true);
                return false;
            } else {
                return true;
            }
        }
    };

    const goToNextStep = useCallback(async () => {
        _.set(submissionVM, 'portalCurrentStep_ML.value', currentStep.mlStepName);
        showProgressDialog(platformMessages.saveSubmission);

        // MUST revert the bypass flag so that PC will remove the eletronic discount if there has no email specified
        _.set(submissionVM, 'bypassValidationForComprater_Ext', false);

        try {
            const submissionData = await LoadSaveService.assignPolicyNumbersToModifiers(submissionVM.value, authHeader);
            _.set(modifiers.lineModifiers_ML, 'value', submissionData.lobData.homeowners.modifiers_ML.lineModifiers_ML);
            syncModifiers(submissionData.lobData.homeowners.modifiers_ML.lineModifiers_ML, modifiers.lineModifiers_ML);
            updateWizardData(submissionVM);
            await saveEarthquakeCoverage();
            const res = await LoadSaveService.updateDraftSubmission(submissionVM.value, authHeader);
            submissionVM.value = res;
            updateWizardData(submissionVM);

            setIsUWIssuesPanelVisible(false);
            if (checkForValidateInfoUwIssues()) {
                return false;
            }

        } catch (error) {
            const bracketContent = error.baseError.match(/\[([^\]]+)\]/);
            if (error.baseError && error.baseError.includes('V214')) {
                if (bracketContent) {
                    showErrorModal('', bracketContent[1], messages.closeWindow);
                }
                return false;
            }else if (error.baseError && error.baseError.indexOf('ConcurrentDataChangeException') > -1) {
                hideProgressDialog();
                showErrorModal(null, messages.somethingWentWrong)
                
                return false;
            } else if(error.baseError){
                if (bracketContent) {
                    showErrorModal('', bracketContent[1], messages.closeWindow);
                }
                return false;
            } else if(error.error.cause) {
                hideProgressDialog();
                showErrorModal(null, error.error.cause.message);
                return false;
            } else {
                throw error;
            }
        } finally {
            if (MLProductCodeUtil.isHomeOwners(productCode)) {
                let hoNextStepName;
                const coverageType = _.get(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value.code', undefined);

                if (coverageType === 'hoprental') {
                    const index = _.findIndex(
                        steps,
                        ({path}) => path === '/comp-home-details'
                    );

                    if (index > 0) {
                        const remainingSteps = _.slice(steps, index + 1);

                        changeNextSteps(remainingSteps);
                    }

                    hoNextStepName = 'quoteCompRaterDetails';
                } else {
                    const index = _.findIndex(
                        steps,
                        ({path}) => path === '/comp-home-details'
                    );

                    if (index > 0) {
                        const remainingSteps = _.slice(steps, index);

                        changeNextSteps(remainingSteps);
                    }

                    hoNextStepName = 'compHoDetails';
                }

                MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, hoNextStepName, isGpa && authHeader);
            }

            hideProgressDialog();
        }

        goNext();

        return false;
    }, [LoadSaveService, authHeader, changeNextSteps, currentStep.mlStepName, goNext, hideProgressDialog, isGpa, productCode, showProgressDialog, steps, submissionVM]);

    const onUpdateErrors_Ext = useCallback((val) => {
        setPriorInsuranceErrors_Ext(val);
    }, []);

    const showPriorInsuranceErrors = () => {
        clearBlockAlerts('priorInsuranceErrors');

        if (priorInsuranceErrors_Ext.length !== 0) {
            priorInsuranceErrors_Ext.forEach((error) => {
                addErrorAlert(error, 'priorInsuranceErrors');
            });
            window.scrollTo(0, 0);
        }
    };

    const skipOnResumeIf = () => {
        return MLMultiLobsDataHolderService.hasCompletedStep(currentStep.mlStepName);
    };

    const fetchData = async() => {
            if (isGpa && (!isSkipping || (isSkipping && !skipOnResumeIf())) && !nextStepVisited()) {
                _.set(submissionVM, 'bypassValidationForComprater_Ext', true);
                showProgressDialog(platformMessages.syncing);

                const submissionData = await LoadSaveService.assignPolicyNumbersToModifiers(submissionVM.value, authHeader);

                _.set(modifiers.lineModifiers_ML, 'value', submissionData.lobData.homeowners.modifiers_ML.lineModifiers_ML);
                syncModifiers(submissionData.lobData.homeowners.modifiers_ML.lineModifiers_ML, modifiers.lineModifiers_ML);
                updateWizardData(submissionVM);
                hideProgressDialog(platformMessages.syncing);
            }        
        submissionVM.value.cleanUpCompRaterMockPolicyNumbers();
        _.set(submissionVM, 'bypassValidationForComprater_Ext', true);

        modifiers.lineModifiers_ML.forEach((mod) => {
            if (mod?.savedFromPortal !== undefined) {
                _.set(mod, 'savedFromPortal.value', true);
                _.set(mod, 'editMode_ML.value', true);
            }
        });
        modifiers.dwellingsModifiers_ML.forEach((mod) => {
            if (mod?.savedFromPortal !== undefined) {
                _.set(mod, 'savedFromPortal.value', true);
                _.set(mod, 'editMode_ML.value', true);
            }
        });

        const initDwellingDateModifiers = () => {
            let date = new Date();
            const dateModifiers = ['zk9gmhjs1bsr143asmo0m9nmlga', 'z50j8jrtptqs14rv13ms7vbppt8', 'zipj8i6jij6ulbada49qcopeota'];
            const modifier = modifiers.dwellingsModifiers_ML.children[0];

            modifiers.dwellingsModifiers_ML.forEach((mod) => {
                if (mod?.patternCode && dateModifiers.includes(mod?.patternCode?.value)) {
                    if (mod.dateValue_ML.year !== undefined && mod.dateValue_ML.month !== undefined && mod.dateValue_ML.day !== undefined) {
                        date = new Date(
                            mod.dateValue_ML.year.value,
                            mod.dateValue_ML.month.value,
                            mod.dateValue_ML.day.value
                        );
                    }

                    _.set(mod, 'chosenTermValue.value', date);
                    _.set(mod, 'dateValue_ML.value', MLLocalDateUtil.date2LocalDate(date));
                }
            });
            updateModifier(modifier, false);
        };

        if ((!isSkipping || (isSkipping && !skipOnResumeIf())) && !nextStepVisited()) {
            initDwellingDateModifiers();
        }
    }

    const updatePPCFields = useCallback(async () => {
        if (!isModernProduct) return;
        const key = `ppcIntegrationCalled_${submissionVM.quoteID.value}`
        if (sessionStorage.getItem(key)) {
            return;
        }
        sessionStorage.setItem(key, 'true');
        const ppcValues = await LoadSaveService.callVeriskIntegration(submissionVM.quoteID.value, authHeader);
        const fireDistrictValues = ppcValues?.fireDistrictNames
        const fireDistrict = ppcValues?.fireDistrictName ?? '';
        setFireDistrictValues(fireDistrictValues);
        setIsFireDistrictEditable(!fireDistrict && fireDistrictValues.length > 1);
    }, [isModernProduct]);

    useEffect(() => {
        fetchData()
        updatePPCFields()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addAlertForEachModifierIssue = useCallback(() => {
        dateModifiersInvalid.forEach((modifier) => {
            const modifierString = translator(messages.dateCannotBePriorYearBuilt, {nameOfModifier: modifier});

            addErrorAlert(modifierString, modifier);
        });
    }, [addErrorAlert, dateModifiersInvalid, translator]);

    const hasInvalidModifier = useCallback(() => {
        const invalidLineMod = modifiers.lineModifiers_ML.children.some((mod) => !mod.aspects.valid || !mod.aspects.subtreeValid);
        const invalidDwellingMod = modifiers.dwellingsModifiers_ML.children.some((mod) => !mod.aspects.valid || !mod.aspects.subtreeValid);

        return invalidLineMod || invalidDwellingMod;
    }, [modifiers.dwellingsModifiers_ML.children, modifiers.lineModifiers_ML.children]);

    const convertToDate = ({ year, month, day }) =>
        new Date(year, month - 1, day);

    const inValidateDateSelected = (dwellingMods) => {
        let hasError = false;
        if(!isModernProduct) return;
        const hasCertifiedIBHS = dwellingMods.value.some(
            (m) =>
                m?.codeIdentifier_ML === 'HOPHasCertifiedIBHS_ML' &&
                (m?.chosenTermValue === true || m?.chosenTermValue === "true")
        );
        const hasWildFirHomeDesginated = dwellingMods.value.some(
            (m) => 
                m?.codeIdentifier_ML === 'HOPIBHSWildfireHomepreparedDesignated_Ext' &&
            (m?.chosenTermValue === true || m?.chosenTermValue === "true")
        )
        dwellingMods.forEach((mod) => {
            if (mod?.value?.codeIdentifier_ML === 'HOPHomePurchaseDate_Ext') {
                const date = submissionVM.baseData?.periodStartDate.value;
                if (mod?.dateValue_ML?.value == undefined) {
                    hasError = false
                }
                else {
                    const selectedDate = convertToDate(mod?.dateValue_ML?.value);
                    const effectiveDate = convertToDate(
                        submissionVM.baseData.periodStartDate.value
                    );
                    if (selectedDate && (selectedDate > effectiveDate)) {
                        addErrorAlert('Home Purchase Date cannot be later than policy effective date', 'purchaseDateError');
                        hasError = true;
                    }
                }
            }
            else if((hasCertifiedIBHS && mod?.value?.codeIdentifier_ML === 'HOPIBHSCertDate_ext') || (hasWildFirHomeDesginated && mod?.value?.codeIdentifier_ML === 'HOPWildfireCertDate_Ext')) {
                const date = submissionVM.baseData?.periodStartDate.value;
                if (mod?.dateValue_ML?.value == undefined) {
                    addErrorAlert('Certicate Expiration Date is required', 'certificateExpirationDateError');
                    hasError = true
                }
                else {
                    const selectedDate = convertToDate(mod?.dateValue_ML?.value);
                    const effectiveDate = convertToDate(
                        submissionVM.baseData.periodStartDate.value
                    );
                    if (selectedDate && (selectedDate <= effectiveDate)) {
                        addErrorAlert('Certification Expiration Date must be later than the transaction effective date.', 'certificateExpirationDateError');
                        hasError = true
                    }
                }
            }
        })

        return hasError;
    };

    const onNext = useCallback(async () => {
        if (isGpa) {
            UserService.getCurrentUser(authHeader).then((user) => {
                HOTrackEvent('Quote Step Submitted', false, submissionVM, user, {step: 'Gateway CompRater HO Validation Page', action: 'CompRater Home Validation Page Submitted'});
            });
        }

        updateInValidOptionSelected(undefined);
        clearErrorAlerts();
        const emailServiceNotDown = await validateEmail();
        if(!emailServiceNotDown)
            return false;

        if (showElectronicToPaperWarning === true && (_.get(submissionVM, 'baseData.policyBillingDocCommPref_ML.value.code') === 'electronic') && !_.get(submissionVM, 'baseData.primaryNamedInsured_ML.emailAddress1.value')) {
            _.set(submissionVM, 'baseData.policyBillingDocCommPref_ML.value', 'paper');
            setShowElectronicToPaperWarning(false);
        }

        clearBlockAlerts('electronicToPaper');
        clearBlockAlerts('certificateExpirationDateError');
        clearBlockAlerts('purchaseDateError');

        _.set(submissionVM, 'lobData.homeowners.coverables.constructionDetails_ML.savedFromPortal.value', true);

        if (submissionVM.baseData.primaryNamedInsured_ML.publicID.value === submissionVM.baseData.accountHolder.publicID.value) {
            submissionVM.baseData.accountHolder.value = submissionVM.baseData.primaryNamedInsured_ML.value;
        }

        const additionalInterests = submissionVM?.lobData?.homeowners?.coverables?.additionalInterests?.value;
        additionalInterests?.forEach((item) => {
            const contact = item?.policyAdditionalInterest;
            if (contact && contact?.policyBillingDocCommPref_Ext === 'email') {
                contact.policyBillingDocCommPref_Ext = undefined;
            }
        });

        if(inValidateDateSelected(modifiers.dwellingsModifiers_ML)){
            setTimeout(()=>{
                setErrorTimestamp(errorTimestamp+1);
            }, 500);
            return false;
        }

        if (!isComponentValid || !submissionVM.aspects.valid || !submissionVM.aspects.subtreeValid) {
            if (priorInsuranceErrors_Ext.length !== 0) {
                showPriorInsuranceErrors()
            }

            setShowErrors(true);
            setTimeout(()=>{
                setErrorTimestamp(errorTimestamp+1);
            }, 500);

            return false;
        }

        const priorDateValidationErrors = validatePriorDateModifiersOnContinue(modifiers.dwellingsModifiers_ML);
        const futureDateValidationErrors = validateFutureDateModifiersOnContinue(modifiers.dwellingsModifiers_ML);

        if (priorDateValidationErrors.length > 0 || futureDateValidationErrors.length > 0) {
            priorDateValidationErrors.forEach((modifier) => {
                const modifierString = translator(messages.dateCannotBePriorYearBuilt, { nameOfModifier: modifier });
                
                addErrorAlert(modifierString, `${modifier} - Prior`);
            });
            futureDateValidationErrors.forEach((modifier) => {
                const modifierString = translator(messages.dateCannotBeAfterTodaysDate, { nameOfModifier: modifier });
                
                addErrorAlert(modifierString, `${modifier} - After`);
            });

            return false;
        }

        if (dateModifiersInvalid.length > 0) {
            addAlertForEachModifierIssue();

            return false;
        }

        if (hasInvalidModifier()) {
            updateShowErrors(true);
            setTimeout(()=>{
                setErrorTimestamp(errorTimestamp+1);
            }, 500);

            return false;
        }

        if(fieldValuesAreNotAvailable()) {
            setTimeout(()=>{
                setErrorTimestamp(errorTimestamp+1);
            }, 500);
            return false;
        }

        if(inValidOptionSelectedNP()) {
            addErrorAlert(translator(messages.inValidOptionselected), 'inValidOptionError');
            setShowErrors(true);
            setTimeout(()=>{
                setErrorTimestamp(errorTimestamp+1);
            }, 500);
            return false;
        }

        if (priorInsuranceErrors_Ext.length !== 0) {
            setShowPriorInsuranceErrors_Ext(true);
            showPriorInsuranceErrors()

            return false;
        }

        MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, 'compHoDetails', authHeader);

        return goToNextStep();

    }, [isGpa, clearErrorAlerts, showElectronicToPaperWarning, submissionVM, clearBlockAlerts, isComponentValid, dateModifiersInvalid.length, hasInvalidModifier, priorInsuranceErrors_Ext.length, currentStep.mlStepName, authHeader, goToNextStep, setShowErrors, errorTimestamp, showPriorInsuranceErrors, addAlertForEachModifierIssue, updateShowErrors]);


    useEffect(() => {
        localStorage.setItem('quoteID', quoteID);
        localStorage.setItem('firstName', submissionVM.baseData.primaryNamedInsured_ML.firstName.value);
    }, [quoteID, submissionVM.baseData.primaryNamedInsured_ML.firstName.value]);

    const getWizardMessage = () => {
        let baseMsg;
        let boldMsg;

        if (MLProductCodeUtil.isHomeOwners(productCode) && !MLProductCodeUtil.isHomeOwnersPartialTypeRental(coveragePartType.code)) {
            baseMsg = messages.baseWizardMessageNonRenters;
            boldMsg = messages.reviewHomeDetails;
        } else if (MLProductCodeUtil.isHomeOwnersPartialTypeRental(coveragePartType.code)) {
            baseMsg = messages.baseWizardMessageRenters;
            boldMsg = messages.reviewQuote;
        }

        if (_.isUndefined(baseMsg) || _.isUndefined(boldMsg)) {
            return undefined;
        }

        return (
            <FormattedMessage
                {...baseMsg}
                values={{
                    boldText: (
                        <strong>{translator(boldMsg)}</strong>
                    )
                }}
            />
        );
    };
    const hasDiscrepancies = () => {
        const lobPendingDiscrepancyItemVMs = getLOBPendingDiscrepancyItemVMs(submissionVM);
        return lobPendingDiscrepancyItemVMs.length > 0;
    };

    useEffect(() => {
            const discrepancyItemVMs = getLOBDiscrepancyItemVMs(submissionVM);
            if (discrepancyItemVMs.length > 0) {
                const lobPendingDiscrepancyItemVMs = getLOBPendingDiscrepancyItemVMs(submissionVM);

                lobPendingDiscrepancyItemVMs.forEach((itemVM) => {
                    discrepancyVMs?.push({
                        publicId: itemVM.publicID.value,
                        itemVM,
                        propertyVMs: getPropertyVMsFromVMItemWithDefaultSelection(itemVM)
                    });
                });
                if(discrepancyVMs !== undefined){
                    setDiscrepancyCategories({
                        dwellings: discrepancyVMs?.filter((discrepacyVM) => isDwellingItemVM(discrepacyVM.itemVM))
                    });
                }
            }

            const initVehicles = _.get(submissionVM, 'lobData.personalAuto.coverables.vehicles.children', []);

            initVehicles.forEach((initVehicleVM) => {
                const initParkingTypeIsUndefined = _.isUndefined(initVehicleVM.parkingType_ML.value);
                const initMileageShouldShow = _.isUndefined(initVehicleVM.annualMileage.value) && milageIsRequired(initVehicleVM);

                const initPurchaseDateIsUndefined = _.isUndefined(initVehicleVM.purchaseDate_ML.value);

                if (initParkingTypeIsUndefined || initMileageShouldShow || initPurchaseDateIsUndefined) {
                    setVehicleHasNonDiscrepancy(true);
                }
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);
    
    // This is for triggering page re-render when user has updated discrepancy value.
    // As requirement needed to show Add Spouse field when Marital status has updated
    // Trigger page re-render to make sure hasSelectedMarriedStatus() is called
    useEffect(() => {
        if (discrepancyUpdated) {
            setDiscrepancyUpdated(false);
        }
    }, [discrepancyUpdated]);

    const handleEligibilityError = (data) => {
        clearBlockAlerts("producerEligibility");
        const defaultReason = translator(messages.selectDiffProducerCode);
        const reasonToShow = data.nonEligibleReason ? data.nonEligibleReason : defaultReason;
        
        addErrorAlert(translator(reasonToShow), 'producerEligibility');

    };

    const requestProducerEligibility = async() => {
            
        const req = {
            state: submissionVM.baseData.policyAddress.state.value.code,
            producerCodeCode: submissionVM.value.baseData.producerCode_ML,
            policyStatus: 'New',
            skipGroupEligibilityCheck : false
        };


        try{
            clearBlockAlerts("producercodechanged");
            
            showProgressDialog(platformMessages.checkingProducerCodeEligibility);

            const data = await MLProducerEligibilityService.isProducerCodeEligible(req, authHeader);

            hideProgressDialog();

            if (!data.isEligible || !_.isUndefined(data.nonEligibleReason)) {
                handleEligibilityError(data);
                
                return false;
            }

            if(data.isEligible && req.producerCodeCode !== data.producerCodeCode){
                _.set(submissionVM,"value.baseData.producerCode_ML",data.producerCodeCode);
                _.set(submissionVM,"value.baseData.producerAutoUpdated_Ext",true);
                updateWizardData(submissionVM);
                
                const errorMessage = translator(messages.producerCodeChanged);
                
                addWarningAlert(errorMessage, 'producercodechanged');
                
            }
        }catch(error){
            console.error(error);
            handleEligibilityError(error);
            return false;
        }
    };

    useEffect(() => {
        if(!skipOnResumeIf()){
            requestProducerEligibility();
        }
    }, []);
    

    const HoHasDiscrepancy = () => {
        try {
            if (!Array.isArray(discrepanciesVM)) return false;
        
            return discrepanciesVM.some((disVM) => {
              try {
                const value = disVM?.itemVM?.roofAge?.value;
                if (!value) return false;
        
                const { quoteValue, hvqValue } = value;
                if (quoteValue == null || hvqValue == null) return false;
        
                return quoteValue !== hvqValue;
              } catch (err) {
                console.error('Error checking item:', err);
                return false;
              }
            });
          } catch (err) {
            console.error('Error in HoHasDiscrepancy:', err);
            return false;
          }
    };

    const getHODiscrepancy = () => {
        if (HoHasDiscrepancy()) {
            const HODiscrepancyVM = discrepanciesVM.find((disVM) => {
                return  disVM.publicId;
            });
            console.log( HODiscrepancyVM.propertyVMs);
            return HODiscrepancyVM.propertyVMs;
        }
    };

    const handleDiscrepancyUpdate = (value, discrepancyPropertyVM, keepHvqValue, driverPublicID)=>{
        if(constructionDetailsVM?.value?.roofAge !== undefined){
            if(value == true){
                  constructionDetailsVM.value.roofAge = discrepancyPropertyVM.value.hvqValue
             }
             else {
                constructionDetailsVM.value.roofAge = discrepancyPropertyVM.value.quoteValue;
             }
        }
        console.log("Updating Value of Roof Age : "+value);
    }

    const isHomeShareAirBnbSelected = () => {
        const isAirBnbSelected = _.get(submissionVM, 'lobData.homeowners.dwellingAdditionalInfo.value[0].answers.HOPDwellingHomeSharingAirbnb_Ext', false);
        return isAirBnbSelected === 'true';
    };

    const getPrimaryNamedInsuredContent = useCallback(() => {
        if (isModernLLCOrLLH && (deedInfo_Ext === true || deedInfo_Ext === 'true')) {
            return translator(messages.firstPersonInControl);
        }
        return translator(messages.primaryNamedInsured);
    }, [isModernLLCOrLLH,
        deedInfo_Ext
    ]);

    const getSecondaryNamedInsuredContent = useCallback(() => {
        if (isModernLLCOrLLH && (deedInfo_Ext === true || deedInfo_Ext === 'true')) {
            return translator(messages.sniPersonInControl);
        }
        return translator(messages.sniNamedInsured);
    }, [isModernLLCOrLLH,
        deedInfo_Ext
    ]);

    const checkSolarPanelField = () => {
        const value = _.get(constructionDetailsVM, 'solarPanelsExistsInd.value');
        if (value) {
            _.set(constructionDetailsVM, 'solarPanelsExists.value', value);
            return true;
        }
        return false;
    }

    const checkCMVValue = () => {
        const value = _.get(constructionDetailsVM, 'currentMarketValue.value');
        if (value && value % 1000 !== 0) {
            return true;
        }
        return false;
    }

    const resetValues = async () => {
        const ppcValues = await LoadSaveService.callVeriskIntegration(
                submissionVM.quoteID.value,
                authHeader
            )

        const newSubmissionVM = viewModelService.clone(submissionVM);

        _.set(
            newSubmissionVM,
            'lobData.homeowners.coverables.constructionDetails_ML.hydrantWithin1000Feet.value',
            ppcValues?.hydrantWithin1000Feet
        );

        _.set(
            newSubmissionVM,
            'lobData.homeowners.coverables.constructionDetails_ML.fireDistrictName.value',
            ppcValues?.fireDistrictName
        );

        const hydrantValue = ppcValues?.hydrantWithin1000Feet;
        const fireDistrict = ppcValues?.fireDistrictName ?? '';
        const fireDistrictValues = ppcValues?.fireDistrictNames

        setSelectedHydrantValue(hydrantValue);
        setSelectedFireDistrictValue(fireDistrict);
        setFireDistrictValues(fireDistrictValues);
        setIsFireDistrictEditable(!fireDistrict && fireDistrictValues.length > 1);
        setIsRefreshButtonVisible(isRefreshBtnVisible());
        setIsHydrantEditable(!isHydrantValueDisabled());


        _.set(newSubmissionVM, 'lobData.homeowners.coverables.constructionDetails_ML.hydrantWithin1000Feet.value', hydrantValue);
        _.set(newSubmissionVM, 'lobData.homeowners.coverables.constructionDetails_ML.fireDistrictName.value', fireDistrict);
        _.set(newSubmissionVM, 'lobData.homeowners.coverables.constructionDetails_ML.isFireDistrictNameEditable.value', !fireDistrict && fireDistrictValues.length > 1);

        updateWizardData(newSubmissionVM);

        refreshData();
    };

    const hasEarthquakeTerm = codeIdentifier =>
        !!getEarthquakeTerm(codeIdentifier);


    const hasEarthquakeTerms = () =>
        !_.isEmpty(getEarthquakeCoverage()?.terms);

    const shouldShowEarthquakeSelection = () => {
        const coverage = getEarthquakeCoverage();

        return !!coverage && (coverage.selected === true || hasEarthquakeTerms());
    };

    const getAvailableTenantTypeList = () => [
        { code: 'credit_check', name: 'Credit check' },
        { code: 'criminal_background_check', name: 'Criminal background check' },
        { code: 'eviction_search', name: 'Eviction search' },
        { code: 'ho4_tenant_policy_on_file', name: 'HO4 tenant policy on file' },
        { code: 'skip_search', name: 'Skip search' },
        { code: 'none_apply', name: 'None apply' },
    ];

    const onDwellingDeedBusinessChange = useCallback((newValue) => {
        const clonedVM = viewModelService.clone(submissionVM);

        _.set(clonedVM, 'baseData.deedInfo_Ext.value', newValue === true);
        _.set(clonedVM, 'value.baseData.deedInfo_Ext', newValue === true);

        updateWizardData(clonedVM);

    }, [submissionVM, updateWizardData, viewModelService]);

    const overrides = {
        emailAddress: {
            visible: isModernHomeOrCondo
        },
        primaryNamedInsuredContainer: {
            visible: isModernProduct
        },
        secondaryNamedInsuredContainer: {
            visible: isModernProduct
        },
        pniEducationLevel: {
            visible: isModernHomeOrCondoOrRenters || isModernLLCOrLLH,
            required: isModernHomeOrCondoOrRenters || isModernLLCOrLLH
        },
        pniOccupation: {
            availableValues: occupationtypelist,
            visible: isModernHomeOrCondoOrRenters || isModernLLCOrLLH,
            required: isModernHomeOrCondoOrRenters || isModernLLCOrLLH
        },
        maritalStatusML: {
            visible: isModernHomeOrCondoOrRenters || isModernLLCOrLLH,
            required: isModernHomeOrCondoOrRenters || isModernLLCOrLLH
        },
        sniEducationLevel: {
            visible: isModernHomeOrCondoOrRenters || isModernLLCOrLLH
        },
        sniOccupation: {
            availableValues: occupationtypelist,
            visible: isModernHomeOrCondoOrRenters || isModernLLCOrLLH
        },
        sniMaritalStatusML: {
            visible: isModernHomeOrCondoOrRenters
        },
        sniRelationship: {
            visible: isModernHomeOrCondoOrRenters
        },
        dwellingDeedBusinessRadioButton:{
            visible: isModernLLCOrLLH,
            onValueChange: onDwellingDeedBusinessChange,
            required: isModernLLCOrLLH
        },
        deedOrTitle: {
            visible: isModernLLCOrLLH && (deedInfo_Ext === true || deedInfo_Ext === 'true')
        },
        primaryNamedInsuredDisplay: { 
            visible: isModernHomeOrCondoOrRenters || isModernLLCOrLLH,
            label: getPrimaryNamedInsuredContent() 
        },
        secondaryNamedInsuredDisplay: { 
            visible: isModernHomeOrCondoOrRenters || isModernLLCOrLLH,
            label: getSecondaryNamedInsuredContent() 
        },
        gpaPageHeader: {
            visible: isGpa
        },
        pageContainer: {
            className: isGpa ? 'page-content-gpa' : undefined
        },
        preQualQuestionSet: {
            path: preQualQuestionSetPath,
            labelPosition: 'top',
            onValueChange: syncPreQualQuestionSets,
            showCompRaterSpacing: true,
            onValidate,
            showErrors,
            checkReadOnlyAnswer
        },
        dwellingQuestionSet: {
            path: 'lobData.homeowners.dwellingAdditionalInfo.value[0]',
            value: filteredDwellingInfoQuestionSetData,
            onValueChange: onDwellingInfoQuestionValueChange,
            onBlur: onBlurDwellingInfoQuestionValueChange,
            showHeaderML: false,
            showCompRaterSpacing: true,
            onValidate,
            showErrors,
            visible: isHomeowners,
            checkReadOnlyAnswer
        },
        lineModifier: {
            modifier: modifiers.lineModifiers_ML,
            showValidationErrors: showErrors,
            onValidate: onValidate,
            updateModifier: updateModifier,
            isCompRaterSpacing: true,
            isHomeowners: true
        },
        dwellingsModifier: {
            modifier: modifiers.dwellingsModifiers_ML,
            showValidationErrors: showErrors,
            onValidate: onValidate,
            updateModifier: updateModifier,
            isHomeowners: true,
            isCompRaterSpacing: true,
            modifierIdToAppend: 'dwellingsMod',
            submissionVM
        },
        constructionModifier: {
            visible: isGpa && isRentalPolicy,
            modifier: modifiers.dwellingConstructionModifiers_ML,
            showValidationErrors: showErrors,
            onValidate: onValidate,
            updateModifier: updateModifier,
            isHomeowners: true,
            isCompRaterSpacing: true,
            modifierIdToAppend: 'dwellingConstructionMod'
        },
        additionalQuestionsContainer: {
            visible: (!isCondoType() || (isCondoType() && isModernProduct)) && !isRentalPolicy
        },
        oilTankUndergroundContainer: {
            visible: _.get(constructionDetailsVM, 'primaryHeatingType.value.code', '') === 'oil',
            onValueChange: writeValue,
            value: _.get(constructionDetailsVM, 'oilTankUnderground.value')
        },
        oilTankDecommissioned: {
            visible: constructionDetailsVM.oilTankUnderground.value === true && !isModernProduct,
            onValueChange: writeValue,
            value: _.get(constructionDetailsVM, 'oilTankDecommissioned.value')
        },
        roofUpgradeDateContainer: {
            visible : (isModernProduct && (isHOPDwellingModernProduct || isHomeOwnersPartialTypeLandlordHome)) || (!isModernProduct)
        },
        roofUpgradeDate: {
            availableValues: getRoofInstalledYear(),
            value: _.get(constructionDetailsVM, "roofUpgradeDateML.year.value"),
            visible:!HoHasDiscrepancy(),
            label: isModernProduct ? translator(messages.roofUpgradeDateForNP) : translator(messages.roofUpgradeDate)
        },
        dwellingAdditionalQuestionSet: {
            path: 'lobData.homeowners.dwellingAdditionalInfo.value[0]',
            value: filteredDwellingAdditionalQuestionSetData,
            onValueChange: onDwellingAdditionalQuestionValueChange,
            onBlur: onBlurDwellingAdditionalQuestionValueChange,
            showHeaderML: false,
            onValidate,
            showErrors,
            visible: isHomeowners
        },
        emailAddress: {
            onValueChange: writeValue,
            onBlur: onEmailAddressBlurred,
            placeholder: isGpa ? translator(messages.enterEmailAddress) : translator(messages.emailAddress),
            disabled: disableEmailField
        },
        emailScriptContainer: {
            visible: false
        },
        priorInsuranceComponent: {
            submissionVM: submissionVM,
            updateWizardData: updateWizardData,
            onValidate: onValidate,
            onUpdateErrors: onUpdateErrors_Ext,
            syncQuestionSets: syncPriorInsuranceQuestionSets,
            showPriorInsuranceErrors: showPriorInsuranceErrors_Ext,
            updateShowPriorInsuranceErrors: setShowPriorInsuranceErrors_Ext,
            showErrors,
            isGpa: true
        },
        priorInsuranceContainer: {
            visible: !isModernProduct
        },
        stickyContinueSection: {
            visible: isGpa,
            defaultContinueSectionId: 'footer',
            nextDescription: getWizardMessage(),
            onNext
        },
        nextDescription: {
            content: getWizardMessage()
        },
        nextButton: {
            disabled: disableNextButton
        },
        uwIssueContainer: {
            visible: isUWIssuesPanelVisible,
            uwIssues: blockingUwIssues,
            jobID: submissionVM.quoteID.value,
            publicID: _.get(submissionVM, 'value.publicID'),
            afterIssuesRefreshedCallback: checkForDisableNextButton,
            submissionVM,
            history
        },
        hoDiscrepancyProperty: {
            visible: HoHasDiscrepancy(),
            discrepancyProperties: getHODiscrepancy(),
            showPropertyGroupDoubleBorderStyle: false,
            discrepancyHasUpdated: (updated) => {
                setDiscrepancyUpdated(updated)
            },
            discrepancyOwnerTitle:"",
            handleDiscrepancyUpdate,
            isHomeowners: true
        },
        dwellingType: {
            visible: isHOPDwellingModernProduct
        },
        currentMarketValue: {
            visible: isModernProduct && _.get(constructionDetailsVM, 'isCurrentMarketValueRequired.value', false),
            required: isModernProduct && _.get(constructionDetailsVM, 'isCurrentMarketValueRequired.value', false),        
            tooltip: {
                text: (<FormattedMessage
                    {...messages.cmvTooltip}
                    values={{
                        br: () => <br />,
                        p: chunks => <p>{chunks}</p>
                    }}
                />)
            }
        },
        dwellingClassification: {
            visible: isModernLLCOrLLH,
            required: isModernLLCOrLLH
        },
        dwellingUnderConstruction: {
            visible: isHOPDwellingModernProduct,
            onValueChange: writeValueNP
        },
        typeofConstruction: {
            visible: isModernProduct && _.get(constructionDetailsVM, 'underconstructionDwelling.value', false)
        },
        includeCovForPPandLossOfUse: {
            visible: isHOPDwellingModernProduct && _.get(constructionDetailsVM, 'underconstructionDwelling.value', false)
        },
        solarPanelsAtPremises: {
            visible: isHOPDwellingModernProduct || (isModernProduct && isHomeOwnersPartialTypeLandlordHome)
        },
        underConstructionNotification: {
            visible: _.get(constructionDetailsVM, 'underconstructionDwelling.value', false)
        },
        homeSharingAirBnbSelectedNotification: {
            visible: isHomeShareAirBnbSelected()
        },
        dwellingConstructionQuestionSet: {
            path: 'lobData.homeowners.dwellingConstructionAdditionalInfo.value[0]',
            value: filteredDwellingConstructionInfoQuestionSetData,
            onValueChange: onDwellingConsstructionInfoValueChange,
            onBlur: onBlurDwellingConstructionAdditionalQuestionValueChange,
            onValidate,
            showErrors,
        },
        secondaryNamedInsuredContainer: {
            visible : isModernProduct && !_.isUndefined(_.get(submissionVM,'baseData.secondaryNamedInsured_ML.value',undefined))
        },
        sniRelationship: {
            visible :  isModernHomeOrCondoOrRenters && !_.isUndefined(_.get(submissionVM,'baseData.secondaryNamedInsured_ML.value',undefined)),
            required : isModernHomeOrCondoOrRenters && !_.isUndefined(_.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value', undefined))
        },
        sniMaritalStatusML: {
            visible : isModernHomeOrCondoOrRenters && !_.isUndefined(_.get(submissionVM,'baseData.secondaryNamedInsured_ML.value',undefined)),
            required : isModernHomeOrCondoOrRenters && !_.isUndefined(_.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value', undefined))
        },
        sniOccupation: {
            availableValues: occupationtypelist,
            visible : isModernProduct && !_.isUndefined(_.get(submissionVM,'baseData.secondaryNamedInsured_ML.value',undefined)),
            required : isModernProduct && !_.isUndefined(_.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value', undefined))
        },
        sniEducationLevel: {
            visible: isModernProduct && !_.isUndefined(_.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value', undefined)),
            required : isModernProduct && !_.isUndefined(_.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value', undefined))
        },
        dateOfBirth: {
            visible: isModernLLCOrLLH && !_.isUndefined(_.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value', undefined)),
            required: isModernLLCOrLLH && !_.isUndefined(_.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value', undefined))
        },
        additionalQsContainer: {
            visible: isModernLLCOrLLH
        },
        numberOfRentalVacantSite: {
            required: isModernLLCOrLLH,
            showValidationErrors: showErrors
        },
        tenantScreeningLabel: {
            visible: isModernLLCOrLLH && !isOccupancyVacant,
        },
        tenantScreening: {
            visible: isModernLLCOrLLH && !isOccupancyVacant,
            required: isModernLLCOrLLH && !isOccupancyVacant,
            showValidationErrors: showErrors,
            availableValues: getAvailableTenantTypeList(),
            onValueChange: values => {
                updateTenantScreeningValues(values);
                _.set(
                    submissionVM,
                    'lobData.homeowners.coverables.constructionDetails_ML.tenantScreening',
                    values
                );
                updateWizardData(submissionVM);
                refreshData();
            },
            value: _.get(
                submissionVM,
                'lobData.homeowners.coverables.constructionDetails_ML.tenantScreening.value',
                []
            ),
            tooltip: {
                text:
                    (<FormattedMessage
                        {...messages.tenantScreeningTooltip}
                        values={{
                            b: chunks => <strong>{chunks}</strong>,
                            br: () => <br />,
                            p: chunks => <p>{chunks}</p>
                        }}
                    />)
            }
        },
        ppcContainer: {
            visible: isModernProduct && (!_.isUndefined(hasSplitPPC) || addressMatch?.toLowerCase() === 'z')
        },

        hydrantWithin1000ft: {
            disabled: !isHydrantEditable,
            value: selectedHydrantValue,
            visible: !_.isUndefined(hasSplitPPC),
            onValueChange: (value) => {
                _.set(
                    constructionDetailsVM,
                    'hydrantWithIn1000Feet.value',
                    value
                );
                setSelectedHydrantValue(value)
                setIsHydrantEditable(!isHydrantValueDisabled())
            }
        },

        fireDistrict: {
            disabled: !isFireDistrictEditable,
            value: fireDistrictDropdownValues.find(item => item.split(';')[0].trim() === fireDistrictName) || fireDistrictName,
            visible: addressMatch?.toLowerCase() === 'z',
            availableValues: fireDistrictDropdownValues.map(item => ({
                code: item,
                name: item,
            })),
            onValueChange: (selectedValue) => {
                setIsFireDistrictEditable(false);
                const selectedFireDistrict =
                    typeof selectedValue === 'string'
                        ? selectedValue
                        : selectedValue?.code || selectedValue?.value || '';

                _.set(
                    constructionDetailsVM,
                    'fireDistrictName.value',
                    selectedFireDistrict
                );
                _.set(
                    constructionDetailsVM,
                    'isFireDistrictNameEditable.value',
                    false
                );
            }
        },
        refreshButton: {
            visible: isRefreshButtonVisible,
            onClick: resetValues,
        },
        earthQuakeContainer: {
            visible: isModernProduct
        },
        limitedOrFull: {
            availableValues: getTermAvailableValues('HOPEarthquakeLimitedorFull_Ext'),
            visible: isEarthquakeSelected() && hasEarthquakeTerm('HOPEarthquakeLimitedorFull_Ext'),
            value: getEarthquakeTerm('HOPEarthquakeLimitedorFull_Ext')?.chosenTerm,
            onValueChange: value =>
                updateEarthquakeTerm('HOPEarthquakeLimitedorFull_Ext', value)
        },
        deductible: {
            availableValues: getTermAvailableValues('HOPEarthquakeDeductible_EXT'),
            visible: isEarthquakeSelected() && hasEarthquakeTerm('HOPEarthquakeDeductible_EXT'),
            value: getEarthquakeTerm('HOPEarthquakeDeductible_EXT')?.chosenTerm,
            onValueChange: value =>
                updateEarthquakeTerm('HOPEarthquakeDeductible_EXT', value)
        },
        structureAttached: {
            value: getEarthquakeTerm('HOPEarthquakeStructureattached_Ext')?.chosenTerm,
            visible: isEarthquakeSelected() && hasEarthquakeTerm('HOPEarthquakeStructureattached_Ext'),
            onValueChange: value =>
                updateEarthquakeTerm('HOPEarthquakeStructureattached_Ext', value)
        },
        mansionCovered: {
            value: getEarthquakeTerm('HOPEarthquakeMasonryVeneer_Ext')?.chosenTerm,
            visible: isEarthquakeSelected() && hasEarthquakeTerm('HOPEarthquakeMasonryVeneer_Ext'),
            onValueChange: value =>
                updateEarthquakeTerm('HOPEarthquakeMasonryVeneer_Ext', value)
        },
        yearBuilt: {
            value: getEarthquakeTerm('HOPEarthquakeYearBuilt_Ext')?.chosenTermValue,
            visible: isEarthquakeSelected() && hasEarthquakeTerm('HOPEarthquakeYearBuilt_Ext'),
            availableValues: getRoofInstalledYear(),
            onValueChange: value =>
                updateEarthquakeTerm('HOPEarthquakeYearBuilt_Ext', value),
            showErrors
        },
        foundationElavated: {
            value: getEarthquakeTerm('HOPEarthquakeCov_ExtHOPEarthquakeFoundationType_Ext')?.chosenTerm,
            visible: isEarthquakeSelected() && hasEarthquakeTerm('HOPEarthquakeCov_ExtHOPEarthquakeFoundationType_Ext'),
            onValueChange: value =>
                updateEarthquakeTerm('HOPEarthquakeCov_ExtHOPEarthquakeFoundationType_Ext', value)
        },
        earthQuakeSelection: {
            value: String(isEarthquakeSelected()),
            visible: shouldShowEarthquakeSelection(),
            onValueChange: async value => {
                const coverage = getEarthquakeCoverage();
                const selected = value === true || value === 'true';

                if (coverage) {
                    _.set(coverage, 'selected', selected);
                    _.set(coverage, 'updated', true);

                    coverage.terms?.forEach(term => {
                        _.set(term, 'updated', true);
                    });

                    if (!selected) {
                        _.set(coverage, 'isValid', true);
                    }
                }
                updateWizardData(submissionVM);
                await saveEarthquakeCoverage();
            }
        },
    };

    const resolvers = {
        resolveComponentMap: {
            modifiercomponent: MLModifierComponent,
            priorinsurancecomponent: PriorInsuranceComponent,
            mluwissuespanelcomponent: MLUWIssuesPanelComponent,
            hoDiscrepancyPropertyComponent: MLHVQDiscrepancyPropertyColomnComponent,
            CheckboxGroupFieldInternal
        },
        resolveCallbackMap: {
            onNext,
            resetValues
        }
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            skipWhen={skipOnResumeIf}
            showNext={!nextStepVisited()}
            onNext={onNext}
            disableNext={disableNextButton}
            nextLabel={translator(platformMessages.mlContinue)}
            showTextNextToButton={!nextStepVisited()}
            textNextToButton={getWizardMessage()}
            metadataPageId={!nextStepVisited() && _.get(metadata, 'pageContent.id')}
            stickyComponentOverrideId='stickyContinueSection'
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                callbackMap={resolvers.resolveCallbackMap}
                model={submissionVM}
                onModelChange={updateWizardData}
                onValueChange={writeValue}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={styles}
                onValidationChange={onValidate}
                showErrors={showErrors}
            />
            <ScrollToError counter={errorTimestamp}/>
        </WizardPage>
    );
}

GatewayCompRaterHOValidateInformationPage.propTypes = wizardProps;
export default withRouter(GatewayCompRaterHOValidateInformationPage);
